import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button, Card, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import Navbar from "../Navigation/Navbar";
import { getAlbumById, getImagesByAlbum } from "../../api/requests";

const ImageManager = () => {
    const { id: albumId } = useParams();
    const navigate = useNavigate();
    const [images, setImages] = useState([]);
    const [album, setAlbum] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [imageData, setImageData] = useState({
        title: "",
        images: [],
        photographer: "",
        description: "",
        is_public: false,
    });

    const token = localStorage.getItem("token");

    useEffect(() => {
        const fetchImagesAndAlbum = async () => {
            try {
                const [imgs, albumData] = await Promise.all([
                    getImagesByAlbum(albumId),
                    getAlbumById(albumId),
                ]);
                setImages(imgs || []);
                setAlbum(albumData || null);
            } catch (err) {
                console.error("Fehler beim Laden der Bilder oder des Albums:", err);
                setImages([]);
                setAlbum(null);
            }
        };
        fetchImagesAndAlbum();
    }, [albumId]);

    const openModal = (image = null) => {
        setSelectedImage(image);
        setImageData(image
            ? {
                title: image.title,
                images: [], // don't prefill file input
                photographer: image.photographer,
                description: image.description,
                is_public: image.is_public,
            }
            : {
                title: "",
                images: [],
                photographer: "",
                description: "",
                is_public: false,
            }
        );
        setShowModal(true);
    };

    const handleFileChange = (e) => {
        setImageData({ ...imageData, images: Array.from(e.target.files) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isUploading) return;
        setIsUploading(true);

        if (selectedImage) {
            // Nur Attribute PATCHen, kein Bild-Upload
            try {
                await axios.patch(
                    `${process.env.REACT_APP_API_URL}/images/${selectedImage.id}/`,
                    {
                        album: albumId,
                        title: imageData.title,
                        photographer: imageData.photographer,
                        description: imageData.description,
                        is_public: imageData.is_public,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                setShowModal(false);
                window.location.reload();
            } catch (err) {
                console.error("Fehler beim Speichern:", err);
            } finally {
                setIsUploading(false);
            }
        } else {
            // Mehrfach-Upload wie gehabt
            try {
                const uploadPromises = imageData.images.map(async (file) => {
                    const formData = new FormData();
                    formData.append("album", albumId);
                    formData.append("title", imageData.title);
                    formData.append("is_public", imageData.is_public);
                    formData.append("photographer", imageData.photographer);
                    formData.append("description", imageData.description);
                    formData.append("image", file);
                    await axios.post(`${process.env.REACT_APP_API_URL}/images/`, formData, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "multipart/form-data",
                        },
                    });
                });
                await Promise.all(uploadPromises);
                setShowModal(false);
                window.location.reload();
            } catch (err) {
                console.error("Fehler beim Speichern:", err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/images/${id}/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setImages(images.filter(img => img.id !== id));
        } catch (err) {
            console.error("Fehler beim Löschen:", err);
        }
    };

    return (
        <>
            <Navbar />
            <Container fluid>
                <Button variant="secondary" className="mb-3" onClick={() => navigate("/admin/home/albums")}>
                    ← Zurück zur Albumverwaltung
                </Button>
                <Card>
                    <Card.Header>
                        <h2>
                            Bilder verwalten
                            {album ? ` (${album.title})` : ` (Album #${albumId})`}
                        </h2>
                    </Card.Header>
                    <Card.Body>
                        <Button className="mb-3" onClick={() => openModal()}>Neue Bilder hochladen</Button>
                        <Row>
                            {(images || []).length === 0 ? (
                                <Col>
                                    <p>Keine Bilder im Album vorhanden.</p>
                                </Col>
                            ) : (
                                (images || []).map((img) => {
                                    // Sichtbarkeitsprüfung
                                    const token = localStorage.getItem("token");
                                    let canSee = img.is_public;
                                    if (token) {
                                        try {
                                            const decoded = JSON.parse(atob(token.split('.')[1]));
                                            if (decoded.is_admin) canSee = true;
                                            else if (img.is_public || decoded) canSee = true;
                                        } catch {}
                                    }
                                    if (!canSee) return null;
                                    return (
                                        <Col key={img.id} xs={12} sm={6} md={4}>
                                            <Card className="mb-3">
                                                <Card.Img variant="top" src={img.image} />
                                                <Card.Body>
                                                    <Card.Title>{img.title}</Card.Title>
                                                    <Card.Text>{img.description}</Card.Text>
                                                    <Card.Text><small>© {img.photographer}</small></Card.Text>
                                                    <Card.Text>Status: {img.is_public ? "Öffentlich" : "Privat"}</Card.Text>
                                                    <Button variant="info" className="me-2" onClick={() => openModal(img)}>Bearbeiten</Button>
                                                    <Button variant="danger" onClick={() => handleDelete(img.id)}>Löschen</Button>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })
                            )}
                        </Row>
                    </Card.Body>
                </Card>
                <Modal show={showModal} onHide={() => setShowModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedImage ? "Bild bearbeiten" : "Neue Bilder hochladen"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-2">
                                <Form.Label>Titel</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={imageData.title}
                                    onChange={(e) => setImageData({ ...imageData, title: e.target.value })}
                                    required
                                    disabled={isUploading}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>Bilder auswählen</Form.Label>
                                <Form.Control
                                    type="file"
                                    multiple={!selectedImage}
                                    onChange={handleFileChange}
                                    required={!selectedImage}
                                    disabled={!!selectedImage}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>Fotograf</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={imageData.photographer}
                                    onChange={(e) => setImageData({ ...imageData, photographer: e.target.value })}
                                    disabled={isUploading}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>Beschreibung</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    value={imageData.description}
                                    onChange={(e) => setImageData({ ...imageData, description: e.target.value })}
                                    disabled={isUploading}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Check
                                    type="checkbox"
                                    label="Öffentlich"
                                    checked={imageData.is_public}
                                    onChange={(e) => setImageData({ ...imageData, is_public: e.target.checked })}
                                    disabled={isUploading}
                                />
                            </Form.Group>
                            <Button className="mt-3" type="submit" disabled={isUploading}>
                                Speichern
                                {isUploading && <Spinner className="ms-2" animation="border" size="sm" />}
                            </Button>
                        </Form>
                    </Modal.Body>
                </Modal>
            </Container>
        </>
    );
};

export default ImageManager;
